import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiSettings, FiX, FiSearch } from "react-icons/fi";
import { BASE_URL } from "../../../utils/constant";
import OrganisationAccessScreen from "./IndividualAccess";
import {
  MdOutlinePersonOutline,
  MdOutlinePersonRemoveAlt1,
} from "react-icons/md";
// Import your alert system - adjust path as needed
import { useAlert } from "components/UI_components/Alert";
import { useNavigate } from "react-router-dom";
import AccountModal from "../../../components/AccountModal";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import { SubscriptionAPI } from "api/dashboard/service";
import LoadingScreen from "components/UI_components/LoadingScreen";

export default function OrganizationTeam() {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState(
    JSON.parse(localStorage.getItem("orgMembers")) || [],
  );
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(
    JSON.parse(localStorage.getItem("user")),
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [currentMember, setCurrentMember] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const navigate = useNavigate();

  const orgName = "Sharma Developer Pvt. Ltd.";
  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

  useEffect(() => {
    // Get the current user's email from localStorage
    const userEmail = JSON.parse(localStorage.getItem("user"))?.user_email;
    if (userEmail && teamMembers.length > 0) {
      const user = teamMembers.find(
        (member) => member.user?.email === userEmail,
      );
      // console.log("Current user:", user);
      if (user) {
        setCurrentMember(user);
      }
    } else {
      console.error("User email not found in localStorage");
    }
  }, [teamMembers]);

  // Uncomment if you have the alert system
  // const { addAlert } = useAlert();

  // Fallback alert function if useAlert is not available
  const { addAlert } = useAlert();
  // Get user details
  async function getUserRole() {
    try {
      const accessToken = localStorage.getItem("access");

      if (accessToken) {
        const response = await fetch(`${BASE_URL}/auth/user-details-of-user/`, {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        });

        const data = await response.json();
        if (data?.status) {
          setUserData(data?.data);
        } else {
          return;
        }
      }
    } catch (error) {
      addAlert(error?.message || "Failed to get user details", "error");
    }
  }

  // Fetch all users for adding members
  const fetchAllUsers = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        return;
      }

      const response = await fetch(`${BASE_URL}/auth/search-users/`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
      });

      const result = await response.json();
      if (!result?.status) {
        addAlert(result?.message || "Failed to fetch users", "error");
        return;
      }

      setAllUsers(result?.data);
    } catch (error) {
      addAlert("Failed to fetch users", "error");
    }
  };

  // Fetch organization members
  const fetchMembers = async () => {
    if (teamMembers.length === 0) {
      setPageLoading(true);
    }
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken || !organizationId) return;

      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch organization members");

      const data = await response.json();
      if (data.status && Array.isArray(data.data)) {
        setTeamMembers(data.data);
        localStorage.setItem("orgMembers", JSON.stringify(data.data));
      } else {
        setTeamMembers([]);
      }
    } catch (err) {
      console.error("Error fetching organization members:", err);
      setTeamMembers([]);
      addAlert("Failed to fetch organization members", "error");
    } finally {
      setPageLoading(false);
    }
  };

  // Add member to organization
  const addMemberToOrganization = async (user) => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        return;
      }
      if (teamMembers.some((m) => m.user?.email === user?.user_email)) {
        addAlert("User is already a member of the organization", "error");
        setLoading(false);
        return;
      }

      // Check active subscription before adding member
      try {
        const subscriptionResponse =
          await SubscriptionAPI.checkUserSubscription(organizationId);
        const hasActiveSubscription =
          subscriptionResponse?.status &&
          subscriptionResponse?.data?.active_flag &&
          subscriptionResponse?.data?.valid_till > new Date().toISOString();

        if (!hasActiveSubscription) {
          addAlert(
            "No active subscription found. Please renew or purchase a plan to add members.",
            "error",
          );
          setLoading(false);
          return;
        }
      } catch (e) {
        // If subscription check fails, block adding member
        addAlert("You don’t have a subscription to add members", "error");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/add-member/`,
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify({
            organization: organizationId,
            user_id: user?.user_id,
            is_admin: false,
            member_control: false,
            joined_as: "member",
            created_by: userData?.first_name,
            last_updated_by: userData?.first_name,
          }),
        },
      );

      const result = await response.json();
      if (!result?.status) {
        if (result?.error === "limit_reached") {
          addAlert(
            result?.message ||
              "You have reached the limit for adding members in your current subscription plan",
            "error",
          );
        } else if (result?.message?.includes("owner")) {
          addAlert(
            result?.message || "Cannot add an owner of organisation",
            "error",
          );
        } else {
          addAlert(result?.message || "Failed to add member", "error");
        }
        setLoading(false);
        return;
      }

      await fetchMembers(); // Refresh members list
      setShowAddMember(false);
      setAddMemberEmail("");
      setFilteredUsers([]);
      addAlert("Member added successfully", "success");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      addAlert("Cannot add an owner of organisation", "error");
    }
  };

  // Handle delete click
  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowConfirmation(true);
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setShowConfirmation(false);
    setMemberToDelete(null);
  };

  // Confirm and execute member deletion
  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        setShowConfirmation(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/member/${memberToDelete.id}/delete/`,
        {
          method: "DELETE",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        },
      );

      if (response.ok) {
        addAlert("Member removed successfully", "success");
        await fetchMembers(); // Refresh the members list
      } else {
        const errorData = await response.json();
        addAlert(errorData?.message || "Failed to remove member", "error");
      }
    } catch (error) {
      addAlert("Error removing member", "error");
      console.error("Error removing member:", error);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setMemberToDelete(null);
    }
  };

  const handleLogout = async () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    };
    try {
      const response = await fetch(
        `${BASE_URL}/token/blacklist/`,
        requestOptions,
      );
      const data = await response.json();
      if (data?.code !== "token_not_valid") {
        localStorage.clear();
        navigate("/login");
      } else {
        console.log("Token not valid");
        localStorage.clear();
        navigate("/login");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getUserDetails = async () => {
    const accessToken = localStorage.getItem("access");
    if (accessToken) {
      const response = await fetch(`${BASE_URL}/auth/user-details-of-user/`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      const data = await response.json();
      if (data?.status) {
        setUserData(data?.data);
        setIsOwner(data?.data?.profession === "owner");
      }
    }
  };

  // Handle email input change for searching users
  const handleInputChange = (e) => {
    setAddMemberEmail(e.target.value);
    const filtered = allUsers.filter(
      (user) => user?.user_email.toLowerCase() === e.target.value.toLowerCase(),
    );
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    getUserRole();
    fetchMembers();
    fetchAllUsers();
    getUserDetails();
  }, []);

  if (!organizationId) {
    return <OrganizationEmptyState />;
  }

  return (
    <div className="w-[84vw] h-[90vh] px-8 py-4 pb-0">
      {/* Loading overlay */}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl p-6 pt-4 w-full max-w-md relative border-2 border-blue-200 animate-fadeIn pointer-events-auto">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl"
              onClick={() => {
                setShowAddMember(false);
                setAddMemberEmail("");
                setFilteredUsers([]);
              }}
            >
              <FiX />
            </button>
            <h2 className="text-lg font-semibold mb-6 text-gray-800 text-center">
              Add New Member
            </h2>
            <label className="block text-gray-600 mb-2 font-medium text-sm">
              Find With EmailID
            </label>
            <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus-within:border-blue-400 mb-2">
              <FiSearch className="text-gray-400 mr-2 w-5 h-5" />
              <input
                type="email"
                className="flex-1 bg-transparent outline-none text-gray-700 placeholder-gray-400 text-base"
                placeholder="Add Mail ID"
                value={addMemberEmail}
                onChange={handleInputChange}
                autoFocus
              />
            </div>

            {/* Display filtered users */}
            {filteredUsers.length > 0 && (
              <div className="mt-4 max-h-40 overflow-y-auto">
                <h3 className="text-sm font-medium mb-2 text-gray-600">
                  Found Users:
                </h3>
                {filteredUsers.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-100 rounded-md p-2 mb-2"
                  >
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm mr-2">
                        {user?.name?.[0]}
                        {user?.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{user?.name}</p>
                        <p className="text-xs text-gray-600">
                          {user?.user_email}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => addMemberToOrganization(user)}
                      className="bg-btn-bg hover:bg-purple-900 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1"
                    >
                      <FiPlus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Confirm Member Removal
            </h3>
            <p className="mb-6">
              Are you sure you want to remove {memberToDelete?.user?.first_name}{" "}
              {memberToDelete?.user?.last_name} from this organization? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 text-white"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedMember ? (
        <>
          <LoadingScreen loading={pageLoading}>
            {/* Current User Card */}
            <div className="bg-white rounded-2xl p-6 flex items-center mb-4 w-full">
              <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-2xl overflow-hidden">
                {currentMember?.user?.userpic ? (
                  <img
                    src={currentMember?.user?.userpic}
                    alt={
                      currentMember?.user?.first_name +
                      " " +
                      currentMember?.user?.last_name
                    }
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : (
                  `${currentMember?.user?.first_name?.[0] || ""}${
                    currentMember?.user?.last_name?.[0] || ""
                  }`
                )}
              </div>
              <div className="ml-6 flex-1">
                <div className="font-medium text-gray-800 text-lg">
                  {currentMember?.user?.first_name}{" "}
                  {currentMember?.user?.last_name} {"(You)"}
                  <span className="ml-2 text-xs items-center font-medium px-2 py-0.5 bg-[#F3F0F5] text-btn-bg rounded-full align-middle">
                    {currentMember?.user?.profession || "Member"}
                  </span>
                  {/* {currentMember?.user?.profession === "owner" && (
                    <span className="ml-2 text-xs items-center font-medium px-2 py-0.5 bg-[#F3F0F5] text-btn-bg rounded-full align-middle">
                      Super Admin
                    </span>
                  )} */}
                </div>
                <div className="text-gray-500 text-sm mt-0.5">
                  {currentMember?.user?.email}
                </div>
              </div>
              <button
                className="border-[1px] border-btn-bg bg-[#ECE6F0] text-btn-bg px-6 py-1.5 rounded-full hover:bg-purple-50 font-medium text-sm flex items-center gap-2 transition-all"
                onClick={() => setIsSettingsOpen(true)}
              >
                <FiSettings className="w-4 h-4 mr-1" /> Manage Account
              </button>
            </div>

            {/* Team Members Section */}
            <div className="bg-white rounded-2xl w-full">
              <div className="flex items-center mx-4 justify-between px-3 mb-2 py-4 border-b-[1px] border-[#C3C3C3]">
                <div className="font-normal text-[#737373]">
                  {`${teamMembers.length} organization member${
                    teamMembers.length !== 1 ? "s" : ""
                  }`}
                </div>
                <button
                  className="bg-btn-bg hover:bg-purple-900 text-white px-3 py-2 rounded-lg font-medium justify-between flex flex-row items-center"
                  onClick={() => setShowAddMember(true)}
                  style={{
                    rowGap: "12px",
                    display:
                      currentMember?.user?.profession === "owner" ||
                      currentMember?.joined_as === "owner"
                        ? "flex"
                        : "none",
                  }}
                >
                  <div className="bg-[#58267F] rounded-full p-1 mr-2">
                    <FiPlus className="" />
                  </div>
                  New Member
                </button>
              </div>
              <div
                className="h-[54vh] overflow-y-scroll"
                style={{
                  scrollbarWidth: "none",
                }}
              >
                {teamMembers?.map((member, index) => (
                  <div
                    key={member.id}
                    className="flex items-center px-4 py-4"
                    style={{
                      borderBottom:
                        index !== teamMembers.length - 1
                          ? "0.5px solid #C3C3C3"
                          : "none",
                    }}
                  >
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-lg overflow-hidden">
                      {member.user?.userpic ? (
                        <img
                          src={member.user?.userpic}
                          alt={
                            member.user?.first_name +
                            " " +
                            member.user?.last_name
                          }
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        `${member.user?.first_name?.[0] || ""}${
                          member.user?.last_name?.[0] || ""
                        }`
                      )}
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <div className="font-medium text-gray-800 text-base flex flex-row items-center">
                        {member.user?.first_name} {member.user?.last_name}
                        <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-[#F3F0F5] text-btn-bg rounded-full">
                          {/* {member?.user?.profession === "owner"
                            ? "Super Admin"
                            : member?.user?.profession || "Member"} */}
                          {member?.user?.profession || "Member"}
                        </span>
                        {member?.is_admin &&
                          member?.user?.profession !== "owner" && (
                            <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                              Admin
                            </span>
                          )}
                      </div>

                      <div
                        className="text-gray-500 text-sm mt-0.5"
                        style={{ maxWidth: "180px" }}
                        title={member.user?.email}
                      >
                        {member.user?.email}
                      </div>
                    </div>

                    <div
                      className="ml-auto flex gap-2 items-center"
                      style={{
                        display:
                          currentMember?.joined_as === "owner" ||
                          currentMember?.is_admin ||
                          currentMember?.member_control
                            ? "flex"
                            : "none",
                      }}
                    >
                      <button
                        style={{
                          display:
                            member?.joined_as === "owner" ||
                            member.user?.profession === "owner" ||
                            member.user?.user_id ===
                              currentMember?.user?.user_id
                              ? "none"
                              : "flex",
                        }}
                        className="border-[1px] border-[#DD2C2C] text-[#DD2C2C] px-6 py-1.5 rounded-full hover:bg-red-50 font-semibold text-sm flex items-center gap-2 transition-all"
                        onClick={() => handleDeleteClick(member)}
                      >
                        <MdOutlinePersonRemoveAlt1 className="w-4 h-4 mr-1" />
                        Remove
                      </button>
                      <button
                        className="border-[1px] border-btn-bg text-btn-bg px-6 py-1.5 rounded-full hover:bg-purple-50 font-semibold text-sm flex items-center gap-2 transition-all"
                        onClick={() => setSelectedMember(member)}
                        style={{
                          display:
                            member?.joined_as === "owner" ||
                            member.user?.profession === "owner"
                              ? "none"
                              : "flex",
                        }}
                      >
                        <MdOutlinePersonOutline className="w-4 h-4 mr-1" />{" "}
                        Manage
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </LoadingScreen>
        </>
      ) : (
        <OrganisationAccessScreen
          member={selectedMember}
          currentOrgMember={currentMember}
          onClose={() => {
            setSelectedMember(null);
            fetchMembers();
          }}
        />
      )}
      {userData?.user && (
        <AccountModal
          isOpen={isSettingsOpen && userData?.user}
          onClose={() => setIsSettingsOpen(false)}
          userData={userData}
          handleLogOut={handleLogout}
          refetch={getUserDetails}
        />
      )}
    </div>
  );
}
